import { Badge, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCards } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AccessRule } from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { AccessRulesEditor } from './AccessRulesEditor';
import { ADMIN_CONTENT_KEY, fetchAdminContent } from './api';
import { setAccessRules } from './content-api';

/**
 * МАК-сессии в администрировании: кому открыт каждый расклад
 * (docs/PAYMENTS.md, §19).
 *
 * Состав тарифов Дарья задаёт сама — как у колод, обучения и игр. Сами
 * расклады (позиции, колоды) здесь не редактируются: только доступ.
 */

const STATUS_LABELS: Record<string, string> = {
  published: 'опубликован',
  draft: 'черновик',
  archived: 'в архиве',
};

export function AdminSpreadsPage() {
  const queryClient = useQueryClient();
  /* Свежий список при каждом заходе — как в карточке пользователя. */
  const query = useQuery({
    queryKey: ADMIN_CONTENT_KEY,
    queryFn: fetchAdminContent,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const content = query.data?.items ?? [];
  const spreads = content.filter((item) => item.kind === 'spread');
  const fullGames = content.filter((item) => item.kind === 'game' && item.variant !== 'demo');

  const access = useMutation({
    mutationFn: (input: { spreadId: string; rules: AccessRule[]; requiredGameId: string | null }) =>
      setAccessRules('spread', input.spreadId, input.rules, input.requiredGameId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_KEY });
      notifications.show({ message: 'Правила доступа сохранены', color: 'teal' });
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="МАК-сессии"
        description="Кому открыт каждый расклад: пробный период, тарифы Лайт и Про, владельцам игры. Изменение сразу действует на всех подписчиков."
      />
      {query.isLoading ? (
        <LoadingState label="Загружаем МАК-сессии..." />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : spreads.length === 0 ? (
        <EmptyState icon={<IconCards size={28} />} title="МАК-сессий пока нет" />
      ) : (
        <Stack gap="lg">
          {spreads.map((spread) => (
            <Paper key={spread.id} withBorder p="lg" data-testid={`admin-spread-${spread.slug}`}>
              <Stack gap="md">
                <Group gap="xs">
                  <Title order={3}>{spread.title}</Title>
                  <Badge variant="light" color={spread.status === 'published' ? 'teal' : 'gray'}>
                    {STATUS_LABELS[spread.status] ?? spread.status}
                  </Badge>
                </Group>
                <Stack gap="xs">
                  <Text fw={600}>Кому доступно</Text>
                  <AccessRulesEditor
                    value={spread.accessRules}
                    requiredGameId={spread.accessRequiredGameId ?? null}
                    games={fullGames}
                    saving={access.isPending && access.variables?.spreadId === spread.id}
                    onSave={(rules, requiredGameId) =>
                      access.mutate({ spreadId: spread.id, rules, requiredGameId })
                    }
                  />
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </>
  );
}
